import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { IProduct, IProductComplianceTechnical, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import type { ButtonVariant } from '@ingka/button-webc';
import { Button as SkapaButton } from '@ingka/button-webc/passive.js';
import { Hyperlink } from '@ingka/common-styles-webc';
import { informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { ModalHeader as SkapaModalHeader, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';

import ProductCardTakeBackReturnInformationModalEnum from '../../enums/ProductCardTakeBackReturnInformationModalEnum.js';
import type IProductCardDexfSettings from '../../types/IProductCardDexfSettings.js';
import type IProductCardTranslations from '../../types/IProductCardTranslations.js';
import ProductCardTakeBackReturnInformationQrCard from './ProductCardTakeBackReturnInformationQrCard.js';
import ProductCardTakeBackReturnInformationStyle from './ProductCardTakeBackReturnInformationStyle.js';

const LIGHTING_MAIN_TYPE_CODES = [
	'00045', // integrated lighting
	'00061', // decorative lighting
	'00161' // wireless lighting
];

/**
 * Inform consumers about take-back/return of IKEA Electrical and Electronic Equipment (EEE) products by a message in relation to the product information and a link to an information page.
 *
 * @event modalOpen Triggered when read more button is clicked and a modal should be opened
 * @event modalClose Triggered when modal close button is clicked and modal should be closed
 */

export default class ProductCardTakeBackReturnInformation extends KompisElement {
	// The translations retrieved from translations service.
	@property({ type: Object, required: true })
	public translations!: IProductCardTranslations;

	// The settings retrieved from settings service.
	@property({ type: Object, required: true })
	public settings!: IProductCardDexfSettings;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Visible modal.
	@property({ type: String })
	public visibleModal: ProductCardTakeBackReturnInformationModalEnum | null = null;

	// Hides read more link. Shows button which opens sheet with QR code instead. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	private sheetElement: SkapaSheet | null = null;

	public override render(): RenderResult {
		if (!this.showTakeBackReturnInformation()) {
			return html`
				<style>
					:host {
						display: none;
					}
				</style>
			`;
		}

		return html`
			<style>
				${ProductCardTakeBackReturnInformationStyle.getStyle()}
			</style>

			${this.getTakeBackReturnInformation()}
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(informationCircle);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define(
			'kompis-product-card-take-back-return-information-qr-card',
			ProductCardTakeBackReturnInformationQrCard
		);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (
				this.visibleModal === ProductCardTakeBackReturnInformationModalEnum.takeBackReturnQrCard
			) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}
		return true;
	}

	private showTakeBackReturnInformation(): boolean {
		return this.settings.kompis.urls.takeBackReturnLink &&
			(this.hasEnergyEfficiencyClass(this.product) || this.isLightingProduct(this.product))
			? true
			: false;
	}

	private isLightingProduct(product: IProduct): boolean {
		const mainTypeCode = product.content?.mainTypeCode;

		if (mainTypeCode && LIGHTING_MAIN_TYPE_CODES.includes(mainTypeCode)) {
			return true;
		}

		return false;
	}

	private hasEnergyEfficiencyClass(product: IProduct): boolean {
		const productEnergyEfficiencyClassHeader = product.content?.complianceTechnical?.find(
			(value: IProductComplianceTechnical) =>
				value.headingType === ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass
		);
		return !!productEnergyEfficiencyClassHeader;
	}

	private getTakeBackReturnInformation(): RenderResult {
		const lightingProduct = this.isLightingProduct(this.product);

		const titleText = lightingProduct
			? this.translations.takeBackReturnLightingTitle
			: this.translations.takeBackReturnKitchenAppliancesTitle;

		const bodyText = lightingProduct
			? this.translations.takeBackReturnLightingBody
			: this.translations.takeBackReturnKitchenAppliancesBody;

		return html` <skapa-inline-message>
			<skapa-icon
				slot="message-icon"
				role="img"
				icon="information-circle"
				aria-label="${this.translations.inlineMessageInformativeAriaLabel}"
				aria-hidden="false"
			></skapa-icon>
			<span slot="title">${titleText}</span>
			<span slot="body">${bodyText}${this.getReadMoreLink()}</span>
			${this.getReadMoreButton()}
		</skapa-inline-message>`;
	}

	private getReadMoreLink(): RenderResult {
		if (this.disableLinks) {
			return null;
		}

		return html` <a
			href="${this.settings.kompis.urls.takeBackReturnLink || ''}"
			target="_blank"
			class="${Hyperlink.classnames()}"
			>${this.translations.takeBackReturnLinkText}</a
		>`;
	}

	private getReadMoreButton(): RenderResult {
		if (!this.disableLinks) {
			return null;
		}

		const tertiaryVariant: ButtonVariant = 'tertiary';
		return html`<skapa-button
			variant="${tertiaryVariant}"
			size="small"
			@click="${this.onReadMoreButtonClick.bind(this)}"
			>${this.translations.takeBackReturnLinkText}</skapa-button
		>`;
	}

	private onReadMoreButtonClick(event: Event): void {
		event.preventDefault();
		this.dispatchEvent(
			new CustomEvent('modalOpen', {
				bubbles: true,
				detail: {
					visibleModal: ProductCardTakeBackReturnInformationModalEnum.takeBackReturnQrCard
				}
			})
		);
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
		}
	}

	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		const sheetElement: SkapaSheet = this.createSheetElement();
		this.sheetElement = sheetElement;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const sortListCardElement = this.createCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(sortListCardElement);
		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		sheetElement.setAttribute('aria-label', this.translations.takeBackReturnLinkText);
		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';
		sheetElement.addEventListener('closerequest', () => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null
						}
					})
				);
			}
		});
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.translations.modalAriaLabelButtonClose;

		sheetHeaderElement.appendChild(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(): ProductCardTakeBackReturnInformationQrCard {
		const cardElement = this.createInternalElement(
			'kompis-product-card-take-back-return-information-qr-card'
		);

		cardElement.translations = this.translations;
		cardElement.settings = this.settings;

		return cardElement;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-card-take-back-return-information': ProductCardTakeBackReturnInformation;
		'skapa-button': SkapaButton;
		'skapa-icon': SkapaIcon;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-sheet': SkapaSheet;
	}
}
